import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { getAllGraphNodes, getGraphNode, createManualLink, deleteManualLink, tableLabel } from './dataGraphApi';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';

const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '—');
const PAGE_SIZE = 10;
const split = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

// "Declare a join the schema doesn't — it becomes a traversal edge." Table with paging + a
// builder row: <from table>.<column> refers to <to table>.<column>. Table names come from the
// graph's node list (datalist); columns from the picked table's detail. Removal is immediate.
// Links belong to ONE data graph (`graphId`); another graph of the same source does not see them.
// `embedded` (2026-11-04): a section of the settings panel (above "What this data graph pulls") instead of
// a panel of its own — the links join the declared lookups when TDM derives an object's rule.
const ManualLinksPanel = ({ dataSourceId, graphId, links, onChanged, embedded = false }) => {
  const canEdit = useCanEdit();
  const [page, setPage] = useState(0);
  const [tables, setTables] = useState([]);
  const [from, setFrom] = useState({ table: '', columns: '' });
  const [to, setTo] = useState({ table: '', columns: '' });
  const [fromCols, setFromCols] = useState([]);
  const [toCols, setToCols] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    // Every page: one page is 1,000 nodes, and a Salesforce profile holds every queryable object.
    getAllGraphNodes(dataSourceId, {})
      .then((res) => {
        if (!alive) return;
        const items = [...(res?.items || [])];
        items.sort((a, b) => tableLabel(a).localeCompare(tableLabel(b)));
        setTables(items);
      })
      .catch(() => { /* datalist is a convenience; typing still works */ });
    return () => { alive = false; };
  }, [dataSourceId]);

  const columnsFor = async (label, setter) => {
    const match = tables.find((t) => tableLabel(t).toLowerCase() === label.toLowerCase() || t.table.toLowerCase() === label.toLowerCase());
    if (!match) { setter([]); return; }
    try {
      const d = await getGraphNode(dataSourceId, match.table, match.schema);
      setter((d?.columns || []).map((c) => c.name));
    } catch (e) {
      setter([]);
    }
  };

  const resolve = (label) => {
    const match = tables.find((t) => tableLabel(t).toLowerCase() === label.toLowerCase() || t.table.toLowerCase() === label.toLowerCase());
    return match ? { schema: match.schema || null, table: match.table } : { schema: null, table: label };
  };

  const add = async () => {
    setError(null);
    const f = resolve(from.table);
    const t = resolve(to.table);
    const fc = split(from.columns);
    const tc = split(to.columns);
    if (!f.table || !t.table || fc.length === 0 || tc.length !== fc.length) {
      setError('Both sides need a table and the same number of columns.');
      return;
    }
    setBusy(true);
    try {
      await createManualLink(dataSourceId, graphId, {
        fromSchema: f.schema, fromTable: f.table, fromColumns: fc, toSchema: t.schema, toTable: t.table, toColumns: tc, note: note || null,
      });
      setFrom({ table: '', columns: '' });
      setTo({ table: '', columns: '' });
      setNote('');
      if (onChanged) await onChanged();
    } catch (e) {
      const msg = e?.body?.message || e?.body?.error || e?.message || 'Could not add the link';
      setError(Number(e?.status) === 409 ? 'That link already exists.' : msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (link) => {
    setBusy(true);
    try {
      await deleteManualLink(dataSourceId, graphId, link.id);
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e?.message || 'Could not remove the link');
    } finally {
      setBusy(false);
    }
  };

  const total = links.length;
  const pageLinks = links.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const first = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className={embedded ? 'dg-section dg-links--embedded' : 'dg-panel dg-panel--links'} data-testid='dg-manual-links'>
      {embedded ? (
        <div className='dg-section__title'
          title='Lookups the schema does not declare (or declares polymorphically) that this data graph should walk anyway: child.column → parent.column. They join the declared lookups when TDM derives how an object is reached.'>
          <span className='dg-help'>Manual links · {total}</span>
        </div>
      ) : (
        <div className='dg-panel__head'>
          <div className='dg-panel__title'>Manual links · {total}</div>
          <div className='dg-muted'>Declare a join the schema doesn&apos;t — it becomes a traversal edge.</div>
        </div>
      )}
      <div className='dg-table' role='table' aria-label='Manual links'>
        <div className='dg-table__head dg-table__head--links' role='row'><span>Link</span><span>Added</span><span /></div>
        {pageLinks.length === 0 && <div className='dg-muted dg-note'>No manual links yet.</div>}
        {pageLinks.map((l) => (
          <div className='dg-table__row dg-table__row--links' role='row' key={l.id} data-testid={`dg-link-${l.id}`}>
            <span>
              <span className='dg-name'>{tableLabel(l.from)}.{(l.fromColumns || []).join(', ')}</span>
              <span className='dg-muted'> → </span>
              <span className='dg-name'>{tableLabel(l.to)}.{(l.toColumns || []).join(', ')}</span>
              {l.resolved === false && <span className='dg-badge dg-badge--rejected' title={l.unresolvedReason}>UNRESOLVED</span>}
              {l.note && <span className='dg-muted'> · {l.note}</span>}
            </span>
            <span className='dg-muted'>{l.createdBy || '—'} · {when(l.createdAt)}</span>
            <span className='dg-actions'>
              <Button size='sm' variant='link' disabled={busy || !canEdit} title={!canEdit ? READ_ONLY_TITLE : undefined} onClick={() => remove(l)} data-testid={`dg-link-remove-${l.id}`}>Remove</Button>
            </span>
          </div>
        ))}
      </div>
      {total > PAGE_SIZE && (
        <div className='dg-pager'>
          <button type='button' className='dg-link' disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
          <span className='dg-muted'>{first}–{last} of {total}</span>
          <button type='button' className='dg-link' disabled={last >= total} onClick={() => setPage(page + 1)}>›</button>
        </div>
      )}
      <div className='dg-builder'>
        <span className='dg-muted'>New link:</span>
        <input className='dg-input' list='dg-tables' placeholder='table' value={from.table}
          onChange={(e) => setFrom({ ...from, table: e.target.value })} onBlur={() => columnsFor(from.table, setFromCols)}
          data-testid='dg-from-table' />
        <span>.</span>
        <input className='dg-input' list='dg-from-cols' placeholder='column' value={from.columns}
          onChange={(e) => setFrom({ ...from, columns: e.target.value })} data-testid='dg-from-column' />
        <span className='dg-muted'>refers to</span>
        <input className='dg-input' list='dg-tables' placeholder='table' value={to.table}
          onChange={(e) => setTo({ ...to, table: e.target.value })} onBlur={() => columnsFor(to.table, setToCols)}
          data-testid='dg-to-table' />
        <span>.</span>
        <input className='dg-input' list='dg-to-cols' placeholder='column' value={to.columns}
          onChange={(e) => setTo({ ...to, columns: e.target.value })} data-testid='dg-to-column' />
        <input className='dg-input dg-input--note' placeholder='note (optional)' value={note} onChange={(e) => setNote(e.target.value)} />
        <Button size='sm' disabled={busy || !canEdit} title={!canEdit ? READ_ONLY_TITLE : undefined} onClick={add} data-testid='dg-add-link'>Add manual link</Button>
        <datalist id='dg-tables'>{tables.map((t) => <option key={tableLabel(t)} value={tableLabel(t)} />)}</datalist>
        <datalist id='dg-from-cols'>{fromCols.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id='dg-to-cols'>{toCols.map((c) => <option key={c} value={c} />)}</datalist>
      </div>
      {error && <div className='dg-error' role='alert'>{error}</div>}
    </div>
  );
};

export default ManualLinksPanel;
